/**
 * Composable for managing parent impurity material relationships in SDS Section 3
 * Handles parent resolution, validation, and UI state management for impurities
 */

import { computed, Ref } from 'vue';
import {
  SdsSection3ImpurityMaterial,
  SdsSection3Material,
} from '@/interfaces/model/general/sds';

/**
 * Parent impurity material info for display purposes
 */
export interface ParentImpurityInfo {
  hasParent: boolean;
  parentMaterialName: string;
  isParentDeleted: boolean;
  parentMaterial?: SdsSection3ImpurityMaterial | SdsSection3Material;
  isParentMaterial?: boolean;
}

/**
 * Available parent impurity option for dropdowns
 */
export interface ParentImpurityOption {
  id: number | string;
  material_name: string;
  isNew: boolean;
  isDeleted: boolean;
  isImpurity: boolean;
}

/**
 * Composable for parent impurity material management
 * @param impurities - Reactive reference to the impurity materials list
 * @param materials - Reactive reference to the materials list (for cross-list parent lookups)
 */
export const useParentImpurities = (
  impurities: Ref<SdsSection3ImpurityMaterial[]>,
  materials: Ref<SdsSection3Material[]>
) => {
  /**
   * Get parent impurity material information for a given impurity
   * Used for display purposes in tables and forms
   * Searches both impurities and materials lists for cross-list parent support
   */
  const getParentImpurityInfo = (
    impurity: SdsSection3ImpurityMaterial
  ): ParentImpurityInfo => {
    let parentItem:
      | SdsSection3ImpurityMaterial
      | SdsSection3Material
      | undefined = undefined;
    let isParentMaterial = false;

    // Check for existing parent first
    if (impurity.parent_impurity_material_id) {
      // First: Try to find in impurities list
      parentItem = impurities.value.find(
        (i) => i.id === impurity.parent_impurity_material_id
      );

      // Second: If not found, search in materials list (cross-list parent)
      if (!parentItem) {
        parentItem = materials.value.find(
          (m) => m.id === impurity.parent_impurity_material_id
        );
        isParentMaterial = !!parentItem;
      }
    }
    // Then check for temp parent (new item scenario)
    else if (impurity.temp_parent_impurity_material_id) {
      // Try impurities first
      parentItem = impurities.value.find(
        (i) =>
          i.temp_impurity_material_id ===
          impurity.temp_parent_impurity_material_id
      );

      // Then try materials (cross-list with temp UUID)
      if (!parentItem) {
        parentItem = materials.value.find(
          (m) =>
            m.temp_material_id === impurity.temp_parent_impurity_material_id
        );
        isParentMaterial = !!parentItem;
      }
    }

    return {
      hasParent: !!parentItem,
      parentMaterialName: parentItem?.material_name || '',
      isParentDeleted: !!parentItem?.deleted_at,
      parentMaterial: parentItem,
      isParentMaterial,
    };
  };

  /**
   * Get available parent items for dropdown selection
   * Includes both impurities and materials to support cross-list parent relationships
   * Excludes the current impurity and its descendants to prevent circular references
   */
  const getAvailableParents = (
    currentImpurity: SdsSection3ImpurityMaterial
  ): ParentImpurityOption[] => {
    // Get impurity options
    const impurityOptions: ParentImpurityOption[] = impurities.value
      .filter((impurity) => {
        // Exclude self
        if (impurity.id === currentImpurity.id && impurity.id !== 0)
          return false;
        if (
          impurity.temp_impurity_material_id ===
            currentImpurity.temp_impurity_material_id &&
          currentImpurity.temp_impurity_material_id
        )
          return false;

        // Exclude deleted impurities
        if (impurity.deleted_at) return false;

        // Exclude descendants
        if (isDescendantOf(currentImpurity, impurity)) return false;

        return true;
      })
      .map((impurity) => ({
        id:
          impurity.id ||
          (impurity.temp_impurity_material_id as string | number),
        material_name: impurity.material_name,
        isNew: impurity.id === 0,
        isDeleted: !!impurity.deleted_at,
        isImpurity: true,
      }));

    // Get material options (cross-list support)
    const materialOptions: ParentImpurityOption[] = materials.value
      .filter((material) => {
        // Exclude deleted materials
        if (material.deleted_at) return false;

        // Check if material is descendant of this impurity (prevent circular)
        if (isDescendantOfMaterial(currentImpurity, material)) return false;

        return true;
      })
      .map((material) => ({
        id: material.id || (material.temp_material_id as string | number),
        material_name: material.material_name,
        isNew: material.id === 0,
        isDeleted: !!material.deleted_at,
        isImpurity: false,
      }));

    // Merge and sort
    return [...impurityOptions, ...materialOptions].sort((a, b) => {
      // Sort: existing first, then new, alphabetically within each group
      if (a.isNew && !b.isNew) return 1;
      if (!a.isNew && b.isNew) return -1;
      return a.material_name.localeCompare(b.material_name);
    });
  };

  /**
   * Check if an impurity is a descendant of another impurity
   * Used to prevent circular references in parent-child relationships
   * Searches both impurities and materials lists
   */
  const isDescendantOf = (
    potentialParent: SdsSection3ImpurityMaterial,
    child: SdsSection3ImpurityMaterial
  ): boolean => {
    const visited = new Set<number | string>();

    const checkCircular = (
      current: SdsSection3ImpurityMaterial | SdsSection3Material
    ): boolean => {
      // Create unique identifier
      const currentId =
        current.id ||
        ('temp_impurity_material_id' in current
          ? (current as SdsSection3ImpurityMaterial).temp_impurity_material_id
          : (current as SdsSection3Material).temp_material_id);

      if (!currentId || visited.has(currentId)) {
        return true; // Circular reference detected
      }
      visited.add(currentId);

      // Get parent ID (works for both materials and impurities)
      const parentId =
        'parent_impurity_material_id' in current
          ? (current as SdsSection3ImpurityMaterial)
              .parent_impurity_material_id ||
            (current as SdsSection3ImpurityMaterial)
              .temp_parent_impurity_material_id
          : (current as SdsSection3Material).parent_material_id ||
            (current as SdsSection3Material).temp_parent_material_id;

      if (!parentId) return false;

      // If parent matches our potential parent, it's a descendant
      const potentialParentId =
        potentialParent.id ||
        (potentialParent.temp_impurity_material_id as string | number);
      if (parentId === potentialParentId) return true;

      // Recursively check parent's parent - search BOTH lists
      const parentInImpurities = impurities.value.find(
        (i) =>
          (i.id && i.id === parentId) ||
          i.temp_impurity_material_id === parentId
      );
      if (parentInImpurities) {
        return checkCircular(parentInImpurities);
      }

      const parentInMaterials = materials.value.find(
        (m) => (m.id && m.id === parentId) || m.temp_material_id === parentId
      );
      if (parentInMaterials) {
        return checkCircular(parentInMaterials);
      }

      return false;
    };

    return checkCircular(child);
  };

  /**
   * Check if a material is a descendant of an impurity
   * Used to prevent circular references when materials have impurity parents
   */
  const isDescendantOfMaterial = (
    currentImpurity: SdsSection3ImpurityMaterial,
    potentialParentMaterial: SdsSection3Material
  ): boolean => {
    const visited = new Set<number | string>();

    const checkCircular = (
      current: SdsSection3Material | SdsSection3ImpurityMaterial
    ): boolean => {
      const currentId =
        current.id ||
        ('temp_material_id' in current
          ? (current as SdsSection3Material).temp_material_id
          : (current as SdsSection3ImpurityMaterial).temp_impurity_material_id);

      if (!currentId || visited.has(currentId)) return true;
      visited.add(currentId);

      // Get parent ID
      const parentId =
        'parent_material_id' in current
          ? (current as SdsSection3Material).parent_material_id ||
            (current as SdsSection3Material).temp_parent_material_id
          : (current as SdsSection3ImpurityMaterial)
              .parent_impurity_material_id ||
            (current as SdsSection3ImpurityMaterial)
              .temp_parent_impurity_material_id;

      if (!parentId) return false;

      // Check if matches current impurity
      const currentImpurityId =
        currentImpurity.id ||
        (currentImpurity.temp_impurity_material_id as string | number);
      if (parentId === currentImpurityId) return true;

      // Recursively check parent - search BOTH lists
      const parentInMaterials = materials.value.find(
        (m) => (m.id && m.id === parentId) || m.temp_material_id === parentId
      );
      if (parentInMaterials) {
        return checkCircular(parentInMaterials);
      }

      const parentInImpurities = impurities.value.find(
        (i) =>
          (i.id && i.id === parentId) ||
          i.temp_impurity_material_id === parentId
      );
      if (parentInImpurities) {
        return checkCircular(parentInImpurities);
      }

      return false;
    };

    return checkCircular(potentialParentMaterial);
  };

  /**
   * Enrich impurities with parent display information
   * Adds computed properties for UI display
   */
  const enrichedImpurities = computed(() => {
    return impurities.value.map((impurity) => {
      const parentInfo = getParentImpurityInfo(impurity);
      return {
        ...impurity,
        hasParent: parentInfo.hasParent,
        parentMaterialName: parentInfo.parentMaterialName,
        isParentDeleted: parentInfo.isParentDeleted,
      };
    });
  });

  return {
    // Core functions
    getParentImpurityInfo,
    getAvailableParents,
    isDescendantOf,
    isDescendantOfMaterial,

    // Computed properties
    enrichedImpurities,
  };
};
